// <PatientAuths practiceId patientId /> — one patient's authorizations, for the
// patient drawer Instance B owns.
//
// B imports one line and renders it; everything else here is C's. Deliberately
// READ-ONLY and self-contained: a drawer is a place to see whether this patient
// has visits left before the next appointment, not a second place to edit an
// authorization. Editing lives on the Authorizations page, which is one click
// away and where the audit trail and the retire confirmation already are.
//
// Renders nothing at all when the patient has no authorizations, so B can mount
// it unconditionally without a "no authorizations" block appearing in every
// drawer for every practice that does not use them.

import React, { useCallback, useEffect, useState } from "react";

import {
  API_URL,
  StatusChip,
  UsageBar,
  formatWindow,
  SourceBadge,
} from "./AuthBits";
import "./AuthorizationsPage.css";

export default function PatientAuths({ practiceId, patientId }) {
  const [rows, setRows] = useState(null); // null = not loaded yet
  const [failed, setFailed] = useState(false);

  // Same `cancelled` guard as the page, and it matters more here: a drawer is
  // clicked down a patient list faster than the network answers, and without it
  // patient A's remaining-visits counter renders under patient B's name.
  const load = useCallback(async (isCancelled) => {
    if (!practiceId || !patientId) return;
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/authorizations?patient_id=${patientId}`,
        { credentials: "include" },
      );
      if (isCancelled()) return;
      if (!response.ok) {
        // 404 means the feature is off for this practice and 403 means this
        // user has patients but not claims — neither is a failure to report in
        // a drawer that is mounted unconditionally, and saying "could not load"
        // in every patient's drawer forever would be noise, not information.
        setFailed(response.status !== 404 && response.status !== 403);
        setRows([]);
        return;
      }
      const payload = await response.json().catch(() => null);
      if (isCancelled()) return;
      // Shape-checked, like the page. A wrong-shape 200 defaulting to [] makes
      // a patient WITH authorizations render no panel at all — and a silently
      // ABSENT visits-remaining counter in a drawer is the worst version of
      // this: there is nothing on screen to be suspicious of.
      if (!payload || !Array.isArray(payload.authorizations)) {
        setFailed(true);
        setRows([]);
        return;
      }
      setRows(payload.authorizations);
    } catch (err) {
      if (isCancelled()) return;
      setFailed(true);
      setRows([]);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setFailed(false);
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  if (rows === null) return null;

  if (failed) {
    // Say so rather than rendering an empty section: "no authorizations" and
    // "we could not check" are different facts, and the second one matters when
    // a biller is deciding whether to see a patient.
    return (
      <div className="auth-drawer">
        <h4 className="auth-drawer-title">Authorizations</h4>
        <p className="auth-drawer-empty">
          Could not load authorizations for this patient.
        </p>
      </div>
    );
  }

  if (rows.length === 0) return null;

  return (
    <div className="auth-drawer">
      <h4 className="auth-drawer-title">
        Authorizations <span className="auth-drawer-count">{rows.length}</span>
      </h4>
      <ul className="auth-drawer-list">
        {rows.map((row) => (
          <li key={row.id} className="auth-drawer-row">
            <div className="auth-drawer-line">
              <span className="auth-number">{row.auth_number}</span>
              <StatusChip status={row.computed_status} />
              <SourceBadge sourceSystem={row.source_system} />
            </div>
            <div className="auth-drawer-line auth-drawer-meta">
              <span>{row.payer_name || "No payer"}</span>
              <span className="auth-alert-sep">·</span>
              <span>{formatWindow(row.effective_from, row.effective_to)}</span>
            </div>
            <UsageBar
              used={row.used}
              remaining={row.remaining}
              authorizedUnits={row.authorized_units}
              status={row.computed_status}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
